import axios from 'axios';

class ExternalApiService {
  constructor() {
    this.timeout = 10000;
  }

  // Wikipedia API with improved error handling
  async searchWikipedia(query, language = 'en', limit = 5) {
    try {
      // Try REST API first
      const searchUrl = `https://${language}.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(query)}`;
      const response = await axios.get(searchUrl, { 
        timeout: this.timeout,
        headers: {
          'User-Agent': 'SahayakFlow/1.0 (Educational Tool)'
        }
      });
      
      return {
        source: 'wikipedia',
        title: response.data.title,
        summary: response.data.extract || 'No summary available',
        url: response.data.content_urls?.desktop?.page || `https://${language}.wikipedia.org/wiki/${encodeURIComponent(query)}`,
        image: response.data.thumbnail?.source,
        data: response.data
      };
    } catch (error) {
      console.warn('Wikipedia REST API failed, trying search API:', error);
      
      // Fallback to search API
      try {
        const searchData = await this.searchWikipediaMultiple(query, language, 1);
        if (searchData && searchData.length > 0) {
          return {
            source: 'wikipedia',
            title: searchData[0].title,
            summary: searchData[0].snippet,
            url: searchData[0].url,
            image: null,
            data: searchData[0]
          };
        }
      } catch (searchError) {
        console.warn('Wikipedia search API also failed:', searchError);
      }
      
      // Final fallback - generate educational placeholder
      return this.generateEducationalPlaceholder(query, language);
    }
  }

  // Generate educational placeholder when APIs fail
  generateEducationalPlaceholder(query, language) {
    const placeholderContent = {
      'en': {
        title: `Educational Content: ${query}`,
        summary: `This is educational content about ${query}. External APIs are currently unavailable, but this represents the type of informative content that would typically be retrieved for educational purposes. In a classroom setting, this would include comprehensive information, definitions, examples, and related concepts to help students understand the topic better.`,
        description: 'Educational resource placeholder'
      },
      'hi': {
        title: `शैक्षिक सामग्री: ${query}`,
        summary: `यह ${query} के बारे में शैक्षिक सामग्री है। बाहरी APIs वर्तमान में अनुपलब्ध हैं, लेकिन यह उस प्रकार की जानकारीपूर्ण सामग्री का प्रतिनिधित्व करता है जो आमतौर पर शैक्षिक उद्देश्यों के लिए प्राप्त की जाती है।`,
        description: 'शैक्षिक संसाधन प्लेसहोल्डर'
      },
      'mr': {
        title: `शैक्षणिक सामग्री: ${query}`,
        summary: `ही ${query} बद्दल शैक्षणिक सामग्री आहे. बाह्य APIs सध्या अनुपलब्ध आहेत, परंतु हे सामान्यतः शैक्षणिक उद्देशांसाठी मिळवल्या जाणार्‍या माहितीपूर्ण सामग्रीच्या प्रकाराचे प्रतिनिधित्व करते.`,
        description: 'शैक्षणिक संसाधन प्लेसहोल्डर'
      }
    };
    
    const content = placeholderContent[language] || placeholderContent['en'];
    
    return {
      source: 'educational-placeholder',
      title: content.title,
      summary: content.summary,
      url: '#educational-placeholder',
      image: null,
      data: { 
        query, 
        language, 
        type: 'fallback',
        message: 'External APIs unavailable - showing educational placeholder'
      }
    };
  }

  async searchWikipediaMultiple(query, language = 'en', limit = 5) {
    try {
      const searchUrl = `https://${language}.wikipedia.org/w/api.php`;
      const response = await axios.get(searchUrl, {
        params: {
          action: 'query',
          format: 'json',
          list: 'search',
          srsearch: query,
          srlimit: limit,
          origin: '*'
        },
        timeout: this.timeout
      });
      
      return response.data.query.search.map(item => ({
        title: item.title,
        snippet: item.snippet.replace(/<[^>]*>/g, ''), // Remove HTML tags
        url: `https://${language}.wikipedia.org/wiki/${encodeURIComponent(item.title)}`,
        pageId: item.pageid
      }));
    } catch (error) {
      throw new Error(`Wikipedia search failed: ${error.message}`);
    }
  }

  // OpenWeatherMap API (if API key provided)
  async getWeatherData(location, apiKey) {
    if (!apiKey) {
      throw new Error('Weather API key not provided');
    }
    
    try {
      const response = await axios.get('https://api.openweathermap.org/data/2.5/weather', {
        params: {
          q: location,
          appid: apiKey,
          units: 'metric'
        },
        timeout: this.timeout
      });
      
      return {
        source: 'openweather',
        location: response.data.name,
        temperature: response.data.main.temp,
        description: response.data.weather[0].description,
        humidity: response.data.main.humidity,
        pressure: response.data.main.pressure,
        data: response.data
      };
    } catch (error) {
      throw new Error(`Weather API failed: ${error.message}`);
    }
  }

  // News API (if API key provided)
  async getNewsData(topic, apiKey, language = 'en') {
    if (!apiKey) {
      throw new Error('News API key not provided');
    }
    
    try {
      const response = await axios.get('https://newsapi.org/v2/everything', {
        params: {
          q: topic,
          apiKey: apiKey,
          language: language,
          sortBy: 'publishedAt',
          pageSize: 5
        },
        timeout: this.timeout
      });
      
      return {
        source: 'newsapi',
        articles: response.data.articles.map(article => ({
          title: article.title,
          description: article.description,
          url: article.url,
          publishedAt: article.publishedAt,
          source: article.source.name
        }))
      };
    } catch (error) {
      throw new Error(`News API failed: ${error.message}`);
    }
  }

  // REST Countries API with fallbacks
  async getCountryInfo(countryName) {
    try {
      const response = await axios.get(`https://restcountries.com/v3.1/name/${encodeURIComponent(countryName)}`, {
        timeout: this.timeout
      });
      
      const country = response.data[0];
      return {
        source: 'restcountries',
        name: country.name.common,
        capital: country.capital?.[0] || 'Not available',
        population: country.population || 0,
        area: country.area || 0,
        region: country.region || 'Unknown',
        subregion: country.subregion || 'Unknown',
        languages: Object.values(country.languages || {}),
        currencies: Object.values(country.currencies || {}),
        flag: country.flags?.png,
        data: country
      };
    } catch (error) {
      console.warn('REST Countries API failed, using fallback:', error);
      
      // Generate country information placeholder
      return this.generateCountryPlaceholder(countryName);
    }
  }

  // Generate country placeholder when API fails
  generateCountryPlaceholder(countryName) {
    const commonCountries = {
      'india': {
        name: 'India',
        capital: 'New Delhi',
        population: 1380000000,
        area: 3287590,
        region: 'Asia',
        subregion: 'Southern Asia',
        languages: ['Hindi', 'English'],
        currencies: ['Indian Rupee']
      },
      'usa': {
        name: 'United States of America',
        capital: 'Washington, D.C.',
        population: 331000000,
        area: 9833520,
        region: 'Americas',
        subregion: 'North America',
        languages: ['English'],
        currencies: ['US Dollar']
      },
      'uk': {
        name: 'United Kingdom',
        capital: 'London',
        population: 67000000,
        area: 242495,
        region: 'Europe',
        subregion: 'Western Europe',
        languages: ['English'],
        currencies: ['British Pound']
      }
    };

    const countryKey = countryName.toLowerCase();
    const countryData = commonCountries[countryKey] || {
      name: countryName,
      capital: 'Information not available',
      population: 0,
      area: 0,
      region: 'Unknown',
      subregion: 'Unknown',
      languages: ['Information not available'],
      currencies: ['Information not available']
    };

    return {
      source: 'country-placeholder',
      ...countryData,
      flag: null,
      data: {
        ...countryData,
        type: 'fallback',
        message: 'Country API unavailable - showing basic information'
      }
    };
  }

  // JSONPlaceholder API (for testing)
  async getPlaceholderData(endpoint) {
    try {
      const response = await axios.get(`https://jsonplaceholder.typicode.com/${endpoint}`, {
        timeout: this.timeout
      });
      
      return {
        source: 'jsonplaceholder',
        endpoint: endpoint,
        data: response.data
      };
    } catch (error) {
      throw new Error(`Placeholder API failed: ${error.message}`);
    }
  }

  // JokesAPI (for fun educational content)
  async getEducationalJoke(category = 'Programming') {
    try {
      const response = await axios.get('https://v2.jokeapi.dev/joke/Programming,Miscellaneous', {
        params: {
          blacklistFlags: 'nsfw,religious,political,racist,sexist,explicit',
          type: 'single'
        },
        timeout: this.timeout
      });
      
      return {
        source: 'jokeapi',
        joke: response.data.joke || `${response.data.setup} ${response.data.delivery}`,
        category: response.data.category,
        data: response.data
      };
    } catch (error) {
      throw new Error(`Jokes API failed: ${error.message}`);
    }
  }

  // Custom API call method
  async callCustomAPI(url, method = 'GET', data = null, headers = {}) {
    try {
      const config = {
        method: method.toUpperCase(),
        url,
        timeout: this.timeout,
        headers: {
          'Content-Type': 'application/json',
          ...headers
        }
      };

      if (data && method.toUpperCase() !== 'GET') {
        config.data = data;
      }

      const response = await axios(config);
      return {
        source: 'custom',
        url,
        status: response.status,
        data: response.data
      };
    } catch (error) {
      throw new Error(`Custom API call failed: ${error.message}`);
    }
  }

  // Educational APIs
  async getQuotesData(category = 'education') {
    try {
      const response = await axios.get('https://api.quotable.io/random', {
        params: {
          tags: category,
          maxLength: 200
        },
        timeout: this.timeout
      });
      
      return {
        source: 'quotable',
        quote: response.data.content,
        author: response.data.author,
        tags: response.data.tags,
        data: response.data
      };
    } catch (error) {
      throw new Error(`Quotes API failed: ${error.message}`);
    }
  }

  // NASA API (if API key provided)
  async getNasaData(apiKey, endpoint = 'apod') {
    if (!apiKey) {
      throw new Error('NASA API key not provided');
    }
    
    try {
      let url = `https://api.nasa.gov/${endpoint}`;
      const params = { api_key: apiKey };
      
      if (endpoint === 'apod') {
        // Astronomy Picture of the Day
        url = 'https://api.nasa.gov/planetary/apod';
      }
      
      const response = await axios.get(url, {
        params,
        timeout: this.timeout
      });
      
      return {
        source: 'nasa',
        endpoint,
        data: response.data
      };
    } catch (error) {
      throw new Error(`NASA API failed: ${error.message}`);
    }
  }

  // Get available API endpoints
  getAvailableAPIs() {
    return {
      free: [
        { name: 'Wikipedia', description: 'Search Wikipedia articles', method: 'searchWikipedia' },
        { name: 'REST Countries', description: 'Get country information', method: 'getCountryInfo' },
        { name: 'Educational Quotes', description: 'Get inspirational quotes', method: 'getQuotesData' },
        { name: 'Educational Jokes', description: 'Get programming/educational jokes', method: 'getEducationalJoke' },
        { name: 'JSONPlaceholder', description: 'Test API endpoints', method: 'getPlaceholderData' }
      ],
      requiresKey: [
        { name: 'OpenWeatherMap', description: 'Weather data', method: 'getWeatherData' },
        { name: 'News API', description: 'Latest news articles', method: 'getNewsData' },
        { name: 'NASA API', description: 'Space and astronomy data', method: 'getNasaData' }
      ],
      custom: [
        { name: 'Custom API', description: 'Call any REST API', method: 'callCustomAPI' }
      ]
    };
  }
}

export default ExternalApiService;